import { supabase } from '@/integrations/supabase/client'
import { logger } from '../logger-service'
import type { ChatRoom } from './types'

export class ChatRoomsService {
  /**
   * Validates and potentially sanitizes a UUID to ensure it matches the standard format expected by Supabase
   * @param id - The ID to validate/sanitize
   * @param context - Additional context for logging
   * @returns The sanitized ID or the original if it appears valid
   */
  private static validateUUID(id: string, context: string = ''): string {
    // Standard UUID format
    const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    
    // Log detailed information about the ID
    logger.debug(`Validating UUID for ${context}`, {
      id,
      length: id.length,
      containsHyphens: id.includes('-'),
      hyphenCount: (id.match(/-/g) || []).length,
      isStandardUUID: UUID_REGEX.test(id)
    });
    
    if (!id || !id.includes('-')) {
      logger.warn(`Non-UUID format detected for ${context}`, { id });
      return id; // Return as is if it doesn't look like a UUID at all
    }
    
    // If it contains too many hyphens, it might be a TalkJS ID that needs cleaning
    if (id.includes('-') && !UUID_REGEX.test(id)) {
      // This is likely a malformed UUID - log it for debugging
      logger.warn(`Potentially malformed UUID for ${context}`, { id });
      
      // Try to extract a valid UUID portion if possible
      // Sometimes IDs from external systems have extra parts
      const possibleUUID = id.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
      if (possibleUUID && possibleUUID[1]) {
        logger.info(`Extracted valid UUID portion from malformed ID for ${context}`, { 
          original: id, 
          extracted: possibleUUID[1] 
        });
        return possibleUUID[1];
      }
    }
    
    return id;
  }

  public static async findOrCreateChatRoom(specialistId: string, caregiverId: string): Promise<ChatRoom> {
    logger.debug(`ChatRoomsService.findOrCreateChatRoom called`, {
      component: 'ChatRoomsService',
      arguments: { specialistId, caregiverId }
    });
    
    try {
      // Validate IDs before using them in queries
      const validSpecialistId = this.validateUUID(specialistId, 'specialist_id');
      const validCaregiverId = this.validateUUID(caregiverId, 'caregiver_id');
      
      // Log the query we're about to make
      logger.debug('Making query to find chat room with parameters', {
        specialist_id: validSpecialistId,
        specialist_id_original: specialistId,
        specialist_id_changed: validSpecialistId !== specialistId,
        caregiver_id: validCaregiverId,
        caregiver_id_original: caregiverId,
        caregiver_id_changed: validCaregiverId !== caregiverId
      });
      
      // First try to find existing room
      const { data: existingRooms, error: findError } = await supabase
        .from('chat_rooms')
        .select('*')
        .eq('specialist_id', validSpecialistId)
        .eq('caregiver_id', validCaregiverId);

      if (findError) {
        logger.error('Error finding chat room', { 
          error: findError,
          specialist_id: validSpecialistId,
          caregiver_id: validCaregiverId
        });
        throw findError;
      }

      // If room exists, return it
      if (existingRooms && existingRooms.length > 0) {
        logger.info('Found existing chat room', { roomId: existingRooms[0].id });
        return existingRooms[0];
      }

      logger.debug('No existing chat room found, creating new one', {
        specialist_id: validSpecialistId,
        caregiver_id: validCaregiverId
      });
      
      // Create new room if none exists
      const { data: newRoom, error: createError } = await supabase
        .from('chat_rooms')
        .insert({
          specialist_id: validSpecialistId,
          caregiver_id: validCaregiverId
        })
        .select()
        .single();

      if (createError) {
        logger.error('Error creating chat room', { 
          error: createError,
          specialist_id: validSpecialistId,
          caregiver_id: validCaregiverId
        });
        throw createError;
      }

      logger.info('Created new chat room', { roomId: newRoom.id });
      return newRoom;
    } catch (error) {
      logger.error('Error in findOrCreateChatRoom', { error });
      throw error;
    }
  }

  public static async createChatRoomOnAssociation(specialistId: string, caregiverId: string): Promise<ChatRoom> {
    try {
      // Check if there's already a specialist-caregiver association
      const { data: association, error: checkError } = await supabase
        .from('specialist_caregiver')
        .select('*')
        .eq('specialist_id', specialistId)
        .eq('caregiver_id', caregiverId)
        .single();

      if (checkError) {
        throw checkError;
      }

      if (!association) {
        throw new Error('No association found between specialist and caregiver');
      }

      // Create or get existing chat room
      return await ChatRoomsService.findOrCreateChatRoom(specialistId, caregiverId);
    } catch (error) {
      logger.error('Error in createChatRoomOnAssociation', { error });
      throw error;
    }
  }
}
